import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";
import { toast } from "react-toastify";
import "./AddGrt.css";

const API_BASE = "https://payyug.in/api";

const AddGrt = () => {
  const superAdminId = Number(localStorage.getItem("adminuserId")) || 14;
  const navigate = useNavigate();
  const formRef = useRef(null);

  const [branches, setBranches] = useState([]);
  const [centers, setCenters] = useState([]);
  const [groups, setGroups] = useState([]);
  const [cgts, setCgts] = useState([]);
  const [reports, setReports] = useState([]);

  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedCgt, setSelectedCgt] = useState(null);

  const [formData, setFormData] = useState({
    grtId:"",
    grtDate: "",
    description: "",
  });

  const [loadingReports, setLoadingReports] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completing, setCompleting] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState("10");
  const [currentPage, setCurrentPage] = useState(1);


  const fetchReportList = async () => {
    setLoadingReports(true);
    try {
      const res = await axios.get(`${API_BASE}/grt/list`, {
        params: { superAdminId },
      });
      setReports(res.data?.data || res.data || []);
    } catch (err) {
      console.error("Failed to load records:", err);
      setReports([]);
    } finally {
      setLoadingReports(false);
    }
  };

  useEffect(() => {
    if (superAdminId) {
      fetchReportList();
    }
  }, [superAdminId, selectedGroup, selectedCgt]);

  useEffect(() => {
    const initData = async () => {
      try {
        const res = await axios.get(`${API_BASE}/get-branch`, {
          params: { SuperAdminId: superAdminId, limit: "all" },
        });
        setBranches(res?.data?.branches || []);
      } catch (err) {
        console.error("Initialization error:", err);
      }
    };
    if (superAdminId) initData();
  }, [superAdminId]);

  // Fetch Centers when Branch changes
  useEffect(() => {
    if (!selectedBranch?.id) return setCenters([]);
    axios
      .get(`${API_BASE}/group/centre`, {
        params: { superAdminId, branchid: selectedBranch.id, limit: 100000 },
      })
      .then((res) => setCenters(res?.data?.data || []))
      .catch((err) => console.error(err));
  }, [superAdminId, selectedBranch]);

  // Fetch Groups when Center changes
  useEffect(() => {
    if (!selectedCenter?.id) return setGroups([]);
    axios
      .get(`${API_BASE}/groupupdate/getall`, {
        params: { superAdminId, CenterId: selectedCenter.id, limit: 100000 },
      })
      .then((res) => setGroups(res.data?.data || []))
      .catch((err) => console.error(err));
  }, [superAdminId, selectedCenter]);

  // Fetch CGTs when Group changes
  useEffect(() => {
    if (!selectedBranch?.id || !selectedCenter?.id || !selectedGroup?.id) {
      setCgts([]);
      setSelectedCgt(null);
      return;
    }
    axios
      .get(`${API_BASE}/cgt/list`, {
        params: {
          superAdminId,
          BranchId: selectedBranch.id,
          CenterId: selectedCenter.id,
          groupId: selectedGroup.id,
          limit: 100000,
        },
      })
      .then((res) => {
        const fetchedCgts = res.data?.data || res.data?.cgts || [];
        setCgts(fetchedCgts);
        if (fetchedCgts.length === 0) {
          setSelectedCgt(null);
        }
      })
      .catch((err) => console.error(err));
  }, [superAdminId, selectedBranch, selectedCenter, selectedGroup]);

  // --- HANDLERS ---

  const handleBranchChange = (e) => {
    const brId = Number(e.target.value);
    setSelectedBranch(branches.find((b) => b.id === brId) || null);
    setSelectedCenter(null);
    setSelectedGroup(null);
    setSelectedCgt(null);
    setCurrentPage(1);
  };

  const handleCenterChange = (e) => {
    const cId = Number(e.target.value);
    setSelectedCenter(centers.find((c) => c.id === cId) || null);
    setSelectedGroup(null);
    setSelectedCgt(null);
    setCurrentPage(1);
  };

  const handleGroupChange = (e) => {
    const gId = Number(e.target.value);
    setSelectedGroup(groups.find((g) => g.id === gId) || null);
    setSelectedCgt(null);
    setCurrentPage(1);
  };

  const resetFormState = () => {
    setSelectedBranch(null);
    setSelectedCenter(null);
    setSelectedGroup(null);
    setSelectedCgt(null);
    setFormData({ grtDate: "", description: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBranch?.id || !selectedCenter?.id || !selectedGroup?.id || !selectedCgt?.id) {
      return toast.warn("Please complete all dropdown selections.");
    }
    if (!formData.grtDate || !formData.description.trim()) {
      return toast.warn("Please fill in GRT Date and Description.");
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/grt/create`, {
        Description: formData.description.trim(),
        GRTTID:formData.grtId,
        GRT_Date: formData.grtDate,
        GRTTakenBy: 1,
        EntryUser: "Admin",
        CGTID: Number(selectedCgt.id),
        groupId: Number(selectedGroup.id),
        superAdminId: Number(superAdminId),
        BranchId: Number(selectedBranch.id),
        CenterId: Number(selectedCenter.id),
        createdBy: superAdminId,
      });
      toast.success(res?.data?.message || "GRT created successfully!");
      resetFormState();
      fetchReportList();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteAssignment = async (id) => {
    if (!window.confirm("Are you sure you want to mark this GRT process as Completed?")) return;

    setCompleting(true);
    try {
      const res = await axios.post(`${API_BASE}/grt/complete`, {
        grtId: Number(id),
        updateBy: Number(superAdminId),
      });

      toast.success(res?.data?.message || "Process marked as completed successfully!");
      fetchReportList();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to complete the task.");
    } finally {
      setCompleting(false);
    }
  };

  const handleDeleteAssignment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    try {
      const res = await axios.delete(`${API_BASE}/grt/delete/${id}`);
      toast.success(res?.data?.message || "Record deleted successfully.");
      setReports((prevReports) => prevReports.filter((report) => report.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    }
  };

  // --- FILTER & PAGINATION LOGIC ---

  const filteredReports = reports.filter((item) => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      String(item.CGTID || "").toLowerCase().includes(q) ||
      String(item.Description || "").toLowerCase().includes(q) ||
      String(item.BranchId || "").includes(q) ||
      String(item.CenterId || "").includes(q) ||
      String(item.groupId || "").includes(q)
    );
  });

  const totalItems = filteredReports.length;
  const parsedSize = itemsPerPage === "all" ? totalItems : Number(itemsPerPage);
  const totalPages = Math.ceil(totalItems / parsedSize) || 1;
  const paginatedReports = filteredReports.slice(
    (currentPage - 1) * parsedSize,
    currentPage * parsedSize,
  );

  return (
    <div className="grt-management-container" ref={formRef}>
      <div className="grt-management-wrapper">
        
        {/* Navigation Header */}
        <div className="grt-management-top-header">
          <ArrowLeftIcon size={18} className="grt-management-top-header-icon" onClick={() => navigate(-1)} />
          <h2 className="grt-management-top-header-title">GRT Management</h2>
          <ArrowRightIcon size={18} className="grt-management-top-header-icon" onClick={() => navigate(1)} />
        </div>

        {/* Form Container */}
        <form onSubmit={handleSubmit} className="grt-management-card">
          <div className="grt-management-inputs-grid">
            {/* Branch Dropdown */}
            <div className="grt-management-form-group">
              <label>Select Branch <span className="grt-management-required">*</span></label>
              <select value={selectedBranch?.id || ""} onChange={handleBranchChange}>
                <option value="">-- Choose Branch --</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>

            {/* Center Dropdown */}
            <div className="grt-management-form-group">
              <label>Select Center <span className="grt-management-required">*</span></label>
              <select value={selectedCenter?.id || ""} disabled={!selectedBranch} onChange={handleCenterChange}>
                <option value="">-- Choose Center --</option>
                {centers.map((c) => <option key={c.id} value={c.id}>{c.CenterName}</option>)}
              </select>
            </div>

            {/* Group Dropdown */}
            <div className="grt-management-form-group">
              <label>Select Group <span className="grt-management-required">*</span></label>
              <select value={selectedGroup?.id || ""} disabled={!selectedCenter} onChange={handleGroupChange}>
                <option value="">-- Choose Group --</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.groupname}</option>)}
              </select>
            </div>

            {/* CGT Dropdown */}
            <div className="grt-management-form-group">
              <label>Select CGT <span className="grt-management-required">*</span></label>
              <select
                value={selectedCgt?.id || ""}
                disabled={!selectedGroup || cgts.length === 0}
                onChange={(e) => setSelectedCgt(cgts.find((x) => x.id === Number(e.target.value)) || null)}
              >
                <option value="">{cgts.length === 0 ? "-- No CGT Available --" : "-- Choose CGT --"}</option>
                {cgts.map((c) => <option key={c.id} value={c.id}>{c.Description}</option>)}
              </select>
            </div>

            {/* GRT ID Input */}
            <div className="grt-management-form-group">
              <label>GRT ID <span className="grt-management-required">*</span></label>
              <input 
                type="number" 
                value={formData.grtId} 
                onChange={(e) => setFormData(prev => ({ ...prev, grtId: e.target.value }))} 
                required 
              />
            </div>
            {/* GRT Date Input */}
            <div className="grt-management-form-group">
              <label>GRT Date <span className="grt-management-required">*</span></label>
              <input 
                type="date" 
                value={formData.grtDate} 
                onChange={(e) => setFormData(prev => ({ ...prev, grtDate: e.target.value }))} 
                required 
              />
            </div>

            {/* Description Input */}
            <div className="grt-management-form-group">
              <label>Description <span className="grt-management-required">*</span></label>
              <input 
                type="text" 
                value={formData.description} 
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} 
                placeholder="Enter GRT description"
                required 
              />
            </div>
          </div>

          <div className="grt-management-form-footer">
            <button type="submit" disabled={submitting} className="grt-management-submit-btn">
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>

        {/* Report List Section Table */}
        <div className="grt-management-report-section">
          <div className="grt-management-report-card-top">
            <h3>GRT Report List</h3>
          </div>

          <div className="grt-management-report-filter-bar">
            <input
              type="text"
              placeholder="Search by ID, Description..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              className="grt-management-search-input"
            />

            <div className="grt-management-limit-container">
              <label>Show Entries:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => { setItemsPerPage(e.target.value); setCurrentPage(1); }}
                className="grt-management-limit-select"
              >
                <option value="10">10 rows</option>
                <option value="25">25 rows</option>
                <option value="50">50 rows</option>
                <option value="all">All</option>
              </select>
            </div>
          </div>

          <div className="grt-management-table-responsive">
            <table className="grt-management-table">
              <thead>
                <tr>
                  <th>Branch ID</th>
                  <th>Center ID</th>
                  <th>Group ID</th>
                  <th>CGT ID</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingReports ? (
                  <tr><td colSpan="7" className="grt-management-table-empty">Loading records from server...</td></tr>
                ) : paginatedReports.length === 0 ? (
                  <tr><td colSpan="7" className="grt-management-table-empty">No Records Found.</td></tr>
                ) : (
                  paginatedReports.map((report) => {
                    const isCompleted = report.status.toLowerCase() === "completed";
                    return (
                      <tr key={report.id}>
                        <td className="grt-management-bold-cell">{report.BranchId || "-"}</td>
                        <td>{report.CenterId || "-"}</td>
                        <td><span className="grt-management-badge">{report.groupId || "-"}</span></td>
                        <td>{report.CGTID || "-"}</td>
                        <td className="grt-management-timestamp">{report.Description || "-"}</td>
                        <td className="grt-management-timestamp">{report.status || "-"}</td>
                        <td>
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button
                              type="button"
                              disabled={isCompleted || completing}
                              onClick={() => handleCompleteAssignment(report.id)}
                              style={{
                                backgroundColor: isCompleted ? "#afebd2" : "#22c55e",
                                color: "#fff",
                                border: "none",
                                padding: "6px 12px",
                                borderRadius: "4px",
                                cursor: isCompleted ? "not-allowed" : "pointer",
                                fontWeight: "bold"
                              }}
                            >
                              {isCompleted ? "Completed" : "Complete"}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteAssignment(report.id)}
                              className="grt-management-btn-del"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {itemsPerPage !== "all" && totalPages > 1 && (
            <div className="grt-management-pagination-container">
              <span className="grt-management-pagination-stats">
                Showing Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> ({totalItems} items total)
              </span>
              <div className="grt-management-pagination-buttons">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1 || loadingReports}
                  className="grt-management-pagination-btn"
                >
                  Prev
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages || loadingReports}
                  className="grt-management-pagination-btn"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddGrt;